import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Edit, Trash2, RefreshCw, MapPin, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { AdminNavbar } from '../../components/custom/AdminNavbar';
import { useAuth } from '../../context/AuthContext';
import { deliveryAPI } from '../../utils/api';
import { toast } from 'sonner';

const AdminDeliveryPostcodes = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [postcodes, setPostcodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPostcode, setEditingPostcode] = useState(null);
  const [formData, setFormData] = useState({
    postcode: '',
    delivery_fee: '',
    minimum_order: '',
    is_active: true
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/admin/login');
      return;
    }
    fetchPostcodes();
  }, [user, navigate]);

  const fetchPostcodes = async () => {
    try {
      const response = await deliveryAPI.getPostcodes();
      setPostcodes(response.data);
    } catch (error) {
      console.error('Failed to fetch postcodes:', error);
      toast.error('Failed to load postcodes');
    } finally {
      setLoading(false);
    }
  };

  const validatePostcode = (postcode) => {
    const cleaned = postcode.trim().toUpperCase().replace(/\s+/g, ' ');
    // Outward only (e.g. DD8, EH1, SW1A) — matches any full postcode in that area.
    const outwardPattern = /^[A-Z]{1,2}[0-9][0-9A-Z]?$/;
    // Sector (e.g. DD8 1, DD81, EH1 2) — matches all postcodes starting with it.
    const sectorPattern = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9]$/;
    // Full postcode (outward + inward).
    const fullPattern = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2}$/;

    if (sectorPattern.test(cleaned)) {
      const normalised = cleaned.includes(' ')
        ? cleaned
        : `${cleaned.slice(0, -1)} ${cleaned.slice(-1)}`;
      return { valid: true, cleaned: normalised };
    }
    if (outwardPattern.test(cleaned)) {
      return { valid: true, cleaned };
    }
    if (fullPattern.test(cleaned)) {
      const normalised = cleaned.includes(' ')
        ? cleaned
        : `${cleaned.slice(0, -3)} ${cleaned.slice(-3)}`;
      return { valid: true, cleaned: normalised };
    }
    return {
      valid: false,
      error: 'Please enter a postcode area (e.g., DD8), a sector (e.g., DD8 1), or a full UK postcode (e.g., DD8 1AA).',
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Validate postcode
    const validation = validatePostcode(formData.postcode);
    if (!validation.valid) {
      setFormError(validation.error);
      return;
    }

    // Validate delivery fee
    const fee = parseFloat(formData.delivery_fee);
    if (isNaN(fee) || fee < 0) {
      setFormError('Please enter a valid delivery fee');
      return;
    }

    // Validate minimum order (optional)
    let minOrder = null;
    if (formData.minimum_order) {
      minOrder = parseFloat(formData.minimum_order);
      if (isNaN(minOrder) || minOrder < 0) {
        setFormError('Please enter a valid minimum order amount');
        return;
      }
    }

    try {
      const data = {
        postcode: validation.cleaned,
        delivery_fee: fee,
        minimum_order: minOrder,
        is_active: formData.is_active
      };

      if (editingPostcode) {
        await deliveryAPI.updatePostcode(editingPostcode.id, data);
        toast.success('Postcode updated successfully');
      } else {
        await deliveryAPI.createPostcode(data);
        toast.success('Postcode added successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchPostcodes();
    } catch (error) {
      console.error('Failed to save postcode:', error);
      const message = error.response?.data?.detail || 'Failed to save postcode';
      setFormError(message);
      toast.error(message);
    }
  };

  const handleEdit = (postcode) => {
    setEditingPostcode(postcode);
    setFormData({
      postcode: postcode.postcode,
      delivery_fee: postcode.delivery_fee.toString(),
      minimum_order: postcode.minimum_order?.toString() || '',
      is_active: postcode.is_active
    });
    setFormError('');
    setDialogOpen(true);
  };

  const handleDelete = async (postcodeId) => {
    if (!window.confirm('Are you sure you want to delete this postcode?')) return;

    try {
      await deliveryAPI.deletePostcode(postcodeId);
      toast.success('Postcode deleted successfully');
      fetchPostcodes();
    } catch (error) {
      console.error('Failed to delete postcode:', error);
      toast.error('Failed to delete postcode');
    }
  };

  const handleToggleActive = async (postcode) => {
    try {
      await deliveryAPI.updatePostcode(postcode.id, {
        is_active: !postcode.is_active
      });
      toast.success(`Postcode ${!postcode.is_active ? 'activated' : 'deactivated'}`);
      fetchPostcodes();
    } catch (error) {
      console.error('Failed to toggle postcode:', error);
      toast.error('Failed to update postcode');
    }
  };

  const resetForm = () => {
    setEditingPostcode(null);
    setFormData({
      postcode: '',
      delivery_fee: '',
      minimum_order: '',
      is_active: true
    });
    setFormError('');
  };

  if (!user || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-background">
      <AdminNavbar title="Delivery Postcodes" />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/admin/dashboard">
            <Button variant="ghost">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={fetchPostcodes} className="rounded-full">
              <RefreshCw className="h-4 w-4" />
            </Button>
            
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-postcode-button" className="rounded-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Postcode
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingPostcode ? 'Edit Postcode' : 'Add Delivery Postcode'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {formError && (
                    <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                      {formError}
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="postcode">Postcode *</Label>
                    <Input
                      data-testid="postcode-input"
                      id="postcode"
                      required
                      placeholder="e.g., DD8, DD8 1, or DD8 1AA"
                      value={formData.postcode}
                      onChange={(e) => setFormData({
                        ...formData, 
                        postcode: e.target.value.toUpperCase()
                      })}
                      className="mt-1.5 uppercase"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter a postcode area (e.g., DD8) to cover all addresses in that area, a sector (e.g., DD8 1) to cover all postcodes starting with it, or a full postcode (e.g., DD8 1AA) for an exact match.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="delivery_fee">Delivery Fee (£) *</Label>
                    <Input
                      data-testid="delivery-fee-input"
                      id="delivery_fee"
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      placeholder="e.g., 2.50"
                      value={formData.delivery_fee}
                      onChange={(e) => setFormData({...formData, delivery_fee: e.target.value})}
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label htmlFor="minimum_order">Minimum Order (£)</Label>
                    <Input
                      data-testid="minimum-order-input"
                      id="minimum_order"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g., 10.00 (optional)"
                      value={formData.minimum_order}
                      onChange={(e) => setFormData({...formData, minimum_order: e.target.value})}
                      className="mt-1.5"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Leave empty for no minimum order requirement
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="is_active">Active</Label>
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                    />
                  </div>

                  <Button data-testid="save-postcode-button" type="submit" className="w-full rounded-full">
                    {editingPostcode ? 'Update Postcode' : 'Add Postcode'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800">Delivery Area Management</h3>
              <p className="text-sm text-blue-700 mt-1">
                Only customers with postcodes listed here can place delivery orders.
                Others will be prompted to choose collection instead.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl h-20 animate-pulse" />
            ))}
          </div>
        ) : postcodes.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl">
            <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium text-muted-foreground">No delivery postcodes yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Add postcodes to enable delivery to specific areas
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {postcodes.map((postcode, index) => (
              <motion.div
                key={postcode.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-xl p-4 shadow-sm border ${
                  !postcode.is_active ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${postcode.is_active ? 'bg-green-50' : 'bg-gray-100'}`}>
                      <MapPin className={`h-5 w-5 ${postcode.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg font-mono">{postcode.postcode}</h3>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>Fee: <strong className="text-primary">£{postcode.delivery_fee.toFixed(2)}</strong></span>
                        {postcode.minimum_order && (
                          <span>Min: <strong>£{postcode.minimum_order.toFixed(2)}</strong></span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(postcode)}
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        postcode.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {postcode.is_active ? (
                        <span className="flex items-center gap-1"><Check className="h-3 w-3" /> Active</span>
                      ) : (
                        <span className="flex items-center gap-1"><X className="h-3 w-3" /> Inactive</span>
                      )}
                    </button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(postcode)}
                      className="h-8 w-8"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDelete(postcode.id)}
                      className="h-8 w-8 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDeliveryPostcodes;
